/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global Office */
import { log } from "../utils/logger";
import { handleMessage } from "../utils/messageHandlers";
import {
  refreshWorkbookAlphaLinks,
  refreshSelectedCellsAlphaLinks,
  refreshWorksheetAlphaLinks,
} from "@alphascriptsolutions/excel-core";
import {
  unlinkAlphaCells,
  unlinkAlphaWorksheet,
  unlinkAlphaWorkbook,
  bootstrapAlphaFunctions,
} from "@alphascriptsolutions/excel-core";
import { isActionActive } from "../components/ShortcutManager";
import { uploadDataToBackend } from "../utils/userUpload";
import { logger } from "@/helpers/tslog";
import { initAlphaAudit, onAlphaAuditClicked } from "@/utils/alphaAudit/index";
import { httpClient } from "@/utils/api/client";

const CAPITALIZATION_TEMPLATE_ID = "ALPHASCRIPT_TEMPLATE_CAPITALIZATION_001";

// Hydrate Office settings from custom XML part embedded by the render agent
async function hydrateAlphaScriptSettings(): Promise<void> {
  try {
    const currentGuid = Office.context.document.settings.get("AlphaScript.workbookGuid");
    if (currentGuid) return;

    const xmlSettings = await Excel.run(async (context) => {
      const parts = context.workbook.customXmlParts;
      parts.load("items");
      await context.sync();

      for (const p of parts.items) {
        const xmlResult = p.getXml();
        await context.sync();
        const xml = xmlResult.value as string | null;
        if (xml && xml.includes("<alphaScriptSettings")) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(xml, "text/xml");
          const ns = "http://alphascript.io/settings";

          const get = (tag: string) => {
            const els = doc.getElementsByTagNameNS(ns, tag);
            return els && els.length > 0 ? (els[0].textContent ?? "") : "";
          };

          const workbookGuid = get("workbookGuid");
          const linkId = get("linkId");
          const chartName = get("chartName") || "Share Price Performance";
          const render = get("render") || "default";

          if (workbookGuid && linkId) {
            return { workbookGuid, linkId, chartName, render };
          }
        }
      }
      return null;
    });

    if (xmlSettings) {
      Office.context.document.settings.set("AlphaScript.workbookGuid", xmlSettings.workbookGuid);
      Office.context.document.settings.set("AlphaScript.links", [xmlSettings]);
      Office.context.document.settings.saveAsync();
    }
  } catch (err) {
    logger.warn("Failed to hydrate AlphaScript settings from XML:", err as any);
  }
}

// Get userId from server
async function getUserId(): Promise<string> {
  try {
    const { data } = await httpClient.get("/user/my-user-id");
    return data.userId;
  } catch (error) {
    logger.warn("Failed to get userId from server:", error);

    return `excel_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }
}

// Build a concise connection payload and commonly used link metadata
async function buildWorkbookConnectionPayload(): Promise<{
  workbookGuid: string;
  hasLinks: boolean;
  linkIds: string[];
  timestamp: string;
  userId: string;
}> {
  const guid = (Office.context.document.settings.get("AlphaScript.workbookGuid") as string) || "unknown";
  const links = (Office.context.document.settings.get("AlphaScript.links") as any[]) || [];
  const linkIds: string[] = links.map((l: any) => l.linkId || l.id).filter(Boolean);
  const userId = await getUserId();

  return {
    workbookGuid: guid,
    hasLinks: linkIds.length > 0,
    linkIds,
    timestamp: new Date().toISOString(),
    userId,
  };
}

// Determine if a pptRefreshRequest targets this workbook/link
function isRequestForThisWorkbook(payload: any): boolean {
  const myGuid = (Office.context.document.settings.get("AlphaScript.workbookGuid") as string) || undefined;
  const myLinks = (Office.context.document.settings.get("AlphaScript.links") as any[]) || [];
  const myLinkIds: string[] = myLinks.map((l: any) => l.linkId || l.id).filter(Boolean);
  const targetGuid = payload?.workbookGuid as string | undefined;
  const targetLinkId = payload?.linkId as string | undefined;
  return !!targetGuid && targetGuid === myGuid && !!targetLinkId && myLinkIds.includes(targetLinkId);
}

Office.onReady(async () => {
  await bootstrapAlphaFunctions({ httpClient });
  // If needed, Office.js is ready to be called.
  // Force full recalculation when workbook opens to ensure all functions are registered
  // Ensure document settings exist by hydrating from the file if needed
  await hydrateAlphaScriptSettings();

  await initAlphaAudit();
  // Ensure this add-in loads automatically with the host (mirrors PowerPoint behavior)
  Office.addin
    .setStartupBehavior(Office.StartupBehavior.load)
    .then(async () => {
      logger.info("[Excel] Startup behavior set to load");
      try {
        const current = await Office.addin.getStartupBehavior();
        logger.info("[Excel] Verified StartupBehavior:", current);
      } catch {}
    })
    .catch((err) => {
      logger.error("[Excel] Failed to set startup behavior", err);
    });

  Office.actions.getShortcuts().then((shortcuts) => {
    logger.debug("Current Shortcut Mappings:", shortcuts);
  });

  /*Excel.run(async (context) => {
    context.workbook.application.calculate(Excel.CalculationType.full);
    await context.sync();
  }).catch((error) => {
    log.error("Failed to perform initial workbook calculation:", error);
  });*/

  // Socket.IO connection for Excel → Server → PowerPoint communication
  try {
    // Skip socket setup when opened by render agent using a dummy GUID
    try {
      const guidForCheck = Office.context.document.settings.get("AlphaScript.workbookGuid") as string | undefined;
      if (typeof guidForCheck === "string" && guidForCheck.startsWith("renderAgent_")) {
        logger.info("[Excel] RenderAgent workbook detected; skipping socket connection");
        return;
      }
    } catch (e) {
      logger.warn("[Excel] GUID check failed before socket connect:", e as any);
    }

    const { default: io } = await import("socket.io-client");
    const apiUrl = process.env.API_URL as string | undefined;
    if (apiUrl) {
      logger.info("[Excel] Attempting Socket.IO connect to:", apiUrl);
      const socket = io(apiUrl, { forceNew: true, reconnection: true });

      socket.on("connect", async () => {
        logger.info("[Excel] Socket connected");

        const payload = await buildWorkbookConnectionPayload();
        socket.emit("workbookConnected", payload);

        // Keep connection alive for ongoing communication
        socket.on("disconnect", (reason) => {
          logger.info("[Excel] Socket disconnected:", reason);
        });

        // Handle refresh request from PowerPoint targeting this workbook/link
        socket.on("pptRefreshRequest", async (payload: any) => {
          try {
            if (!isRequestForThisWorkbook(payload)) {
              return;
            }
            logger.info("[Excel] Received pptRefreshRequest for this workbook/link");
            // Try to fetch the actual chart image from the workbook
            const base64 = await getChartFromWorkbookForLinkIdSendToPowerPoint(payload?.linkId, payload?.userId);

            const workbookGuid =
              (Office.context.document.settings.get("AlphaScript.workbookGuid") as string) || undefined;
            const responsePayload = base64
              ? {
                  success: true,
                  workbookGuid,
                  linkId: payload?.linkId,
                  userId: payload?.userId,
                  contentType: "image/png",
                  contentEncoding: "base64",
                  content: base64,
                  chartName: "Share Price Performance",
                  timestamp: new Date().toISOString(),
                }
              : {
                  success: false,
                  workbookGuid,
                  linkId: payload?.linkId,
                  userId: payload?.userId,
                  error: "Failed to capture chart image from Excel",
                  chartName: "Share Price Performance",
                  timestamp: new Date().toISOString(),
                };

            socket.emit("pptRefreshResponse", responsePayload);
          } catch (e) {
            console.warn("[Excel] Failed handling pptRefreshRequest", e);
          }
        });
      });

      socket.on("connect_error", (err) => {
        logger.warn("[Excel] Socket connect_error:", err?.message || err);
        socket.disconnect();
      });

      // Notify PowerPoint via server when this workbook is about to close/unload
      // Skip for render-agent workbooks (dummy GUIDs) to avoid killing real sessions
      try {
        const guidForListener = Office.context.document.settings.get("AlphaScript.workbookGuid") as string | undefined;
        if (!guidForListener || guidForListener.startsWith("renderAgent_")) {
          logger.info("[Excel] Skipping beforeunload disconnect for renderAgent workbook");
        } else {
          logger.info("[Excel] Registering beforeunload listener for disconnect emit");
          window.addEventListener("beforeunload", () => {
            try {
              const guid = (Office.context.document.settings.get("AlphaScript.workbookGuid") as string) || "";
              if (typeof guid === "string" && !guid.startsWith("renderAgent_")) {
                const payload = { workbookGuid: guid, timestamp: new Date().toISOString() };
                socket.emit("workbookDisconnected", payload);
              }
            } catch (err) {
              logger.warn("[Excel] beforeunload handler failed:", err as any);
            }
          });
        }
      } catch (e) {
        logger.warn("[Excel] Failed to set beforeunload listener:", e as any);
      }
    }
  } catch (e) {
    logger.warn("[Excel] Socket.IO setup failed:", e);
  }
});

// Capture a chart image from the worksheet named "Share Price Performance",
// scaling its intrinsic size by 5x before calling getImage(width, height).
async function getChartFromWorkbookForLinkIdSendToPowerPoint(linkId?: string, userId?: string): Promise<string | null> {
  try {
    const imageBase64 = await Excel.run(async (context) => {
      const worksheetName = "Share Price Performance";

      const worksheet = context.workbook.worksheets.getItem(worksheetName);
      worksheet.load("name");

      const charts = worksheet.charts;
      charts.load("items");
      await context.sync();

      if (!charts.items || charts.items.length === 0) {
        logger.warn(`[Excel] No charts found on worksheet: ${worksheetName}`);
        return null as unknown as string | null;
      }

      const chart = charts.items[0];
      chart.load(["name", "height", "width"]);
      await context.sync();

      const originalWidth = typeof chart.width === "number" ? chart.width : 0;
      const originalHeight = typeof chart.height === "number" ? chart.height : 0;

      const targetWidth = Math.max(1, Math.round(originalWidth * 5));
      const targetHeight = Math.max(1, Math.round(originalHeight * 5));

      const imageResult = chart.getImage(targetWidth, targetHeight, Excel.ImageFittingMode.fit);
      await context.sync();

      const base64 = imageResult.value as string;
      logger.info(
        `[Excel] Captured chart image from '${worksheetName}' (chart='${chart.name}', size=${originalWidth}x${originalHeight} → ${targetWidth}x${targetHeight})`,
        { linkId, userId }
      );

      return base64;
    });

    return imageBase64;
  } catch (error) {
    logger.warn("[Excel] Failed to capture chart image locally:", error as any);
    return null;
  }
}

/**
 * Shows a notification when the add-in command is executed.
 * @param event
 */
function action(event: Office.AddinCommands.Event) {
  const message: Office.NotificationMessageDetails = {
    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
    message: "Performed action.",
    icon: "Icon.80x80",
    persistent: true,
  };

  // Show a notification message.
  Office.context.mailbox.item?.notificationMessages.replaceAsync("ActionPerformanceNotification", message);

  // Be sure to indicate when the add-in command function is complete.
  event.completed();
}

// Register the function with Office.
Office.actions.associate("action", action);

let dialog: Office.Dialog;
let selectionHandler: OfficeExtension.EventHandlerResult<Excel.SelectionChangedEventArgs>;

export function openPopup(event?: Office.AddinCommands.Event) {
  const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
  const popupUrl = `${frontendUrl}popup.html`;

  localStorage.removeItem("placeholder");
  localStorage.removeItem("cellAddress");
  localStorage.removeItem("redirectTo");

  // Retrieve the cell the user was before clicking the ribbon button
  Excel.run(async (context) => {
    const range = context.workbook.getActiveCell();
    range.load("address");
    await context.sync();
    if (range) {
      localStorage.setItem("activeCell", range.address.split("!")[1]);
    }
  });

  Office.context.ui.displayDialogAsync(popupUrl, { height: 75, width: 70, displayInIframe: true }, (result) => {
    if (result.status === Office.AsyncResultStatus.Failed) {
      log.error("Failed to open dialog:", result.error.message);
    } else {
      dialog = result.value;
      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        async (args: Office.DialogParentMessageReceivedEventArgs) => await handleMessage(args, dialog, selectionHandler)
      );
    }
    // Important: Signal that the function is done.
    if (event) {
      event.completed();
    }
  });
}
Office.actions.associate("openPopup", openPopup);

async function startProgressPopup(onProgressReady: () => Promise<void>) {
  await Office.onReady();
  const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
  const popupUrl = `${frontendUrl}progress-popup.html`;
  Office.context.ui.displayDialogAsync(popupUrl, { height: 10, width: 10, displayInIframe: true }, async (result) => {
    if (result.status === Office.AsyncResultStatus.Failed) {
      log.error("Failed to open dialog:", result.error.message);
    } else {
      const dialogRef = result.value;
      dialogRef.addEventHandler(
        Office.EventType.DialogMessageReceived,
        async (arg: { message?: string; error?: number }) => {
          if ("message" in arg) {
            if (arg.message === "ready") {
              await onProgressReady();
              await new Promise((resolve) => setTimeout(resolve, 2000));
              dialogRef.messageChild("done");
            } else if (arg.message === "done") {
              dialogRef.close();
            }
          }
        }
      );
    }
  });
}

async function workbookHasCapitalizationTemplateId(): Promise<boolean> {
  try {
    const found = await Excel.run(async (context) => {
      const parts = context.workbook.customXmlParts;
      parts.load("items");
      await context.sync();

      for (const p of parts.items) {
        const xmlResult = p.getXml();
        await context.sync();
        const xml = xmlResult.value as string | null;
        if (xml && xml.includes("<alphaScriptTemplate")) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(xml, "text/xml");
          const ns = "http://alphascript.io/template";
          const els = doc.getElementsByTagNameNS(ns, "templateId");
          const id = els && els.length > 0 ? (els[0].textContent ?? "") : "";
          if (id === CAPITALIZATION_TEMPLATE_ID) {
            return true;
          }
        }
      }
      return false;
    });

    if (found) {
      logger.info("[Excel] Found capitalization template ID in customXml");
    } else {
      logger.info("[Excel] Capitalization template ID not found in customXml");
    }
    return found;
  } catch (e) {
    logger.warn("[Excel] Failed to inspect customXml for capitalization template ID:", e as any);
    return false;
  }
}

export async function uploadDataPopup(event?: Office.AddinCommands.Event) {
  try {
    const hasTemplateId = await workbookHasCapitalizationTemplateId();
    if (!hasTemplateId) {
      logger.info("[Excel] Workbook does not have capitalization template ID; showing upload template error popup");

      const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
      const dialogUrl = `${frontendUrl}error-popup.html`;
      Office.context.ui.displayDialogAsync(dialogUrl, { height: 35, width: 30, displayInIframe: true }, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          log.error("Failed to open upload-data error dialog:", result.error.message);
        } else {
          const errorDialog = result.value;
          errorDialog.addEventHandler(Office.EventType.DialogMessageReceived, (args: any) => {
            handleMessage(args, errorDialog, undefined);
          });
        }
      });
      return;
    }

    log.info(process.env.REACT_APP_PUBLIC_URL);
    const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
    const popupUrl = `${frontendUrl}upload-data.html`;

    Office.context.ui.displayDialogAsync(popupUrl, { height: 60, width: 40, displayInIframe: true }, (result) => {
      log.info(result);
      if (result.status === Office.AsyncResultStatus.Failed) {
        log.error("Failed to open dialog:", result.error.message);
      } else {
        const popupDialog = result.value;
        popupDialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args: any) => {
          log.info("Message received from popup:", args.message);
          await handleMessage(args, popupDialog, undefined);
        });
      }
      if (event) {
        event.completed();
      }
    });
  } catch (e) {
    log.error("uploadDataPopup failed:", e);
    if (event) {
      event.completed();
    }
  }
}

export async function refreshSelection(event?: Office.AddinCommands.Event) {
  try {
    await Office.onReady();
    log.info("Refreshing selection");

    startProgressPopup(async () => {
      await refreshSelectedCellsAlphaLinks();
    });

    if (event) {
      event.completed();
    }
  } catch (error) {
    log.error("Failed to refresh cell:", error);
    event.completed();
  }
}

export async function refreshWorksheet(event?: Office.AddinCommands.Event) {
  try {
    await Office.onReady();
    log.info("Refreshing worksheet");
    startProgressPopup(async () => {
      await refreshWorksheetAlphaLinks();
    });

    if (event) {
      event.completed();
    }
  } catch (error) {
    log.error("Failed to refresh worksheet:", error);
    event.completed();
  }
}

export async function refreshWorkbook(event?: Office.AddinCommands.Event) {
  try {
    await Office.onReady();
    log.info("Refreshing workbook");

    startProgressPopup(async () => {
      await refreshWorkbookAlphaLinks();
    });

    if (event) {
      event.completed();
    }
  } catch (error) {
    log.error("Failed to refresh workbook:", error);
    event.completed();
  }
}

export async function unlinkSelection(event?: Office.AddinCommands.Event) {
  try {
    await Office.onReady();
    await unlinkAlphaCells();
    event.completed();
  } catch (error) {
    log.error("Failed to unlink selection:", error);
    event.completed();
  }
}

export async function unlinkWorksheet(event?: Office.AddinCommands.Event) {
  try {
    await Office.onReady();
    startProgressPopup(async () => {
      await unlinkAlphaWorksheet();
    });
    event.completed();
  } catch (error) {
    log.error("Failed to unlink worksheet:", error);
    event.completed();
  }
}

export async function unlinkWorkbook(event?: Office.AddinCommands.Event) {
  try {
    await Office.onReady();
    startProgressPopup(async () => {
      await unlinkAlphaWorkbook();
    });
    event.completed();
  } catch (error) {
    log.error("Failed to unlink workbook:", error);
    event.completed();
  }
}

let popupDialog: Office.Dialog;
export function formatTemplatePopup(event?: Office.AddinCommands.Event) {
  log.info(process.env.REACT_APP_PUBLIC_URL);
  const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
  const popupUrl = `${frontendUrl}formatTemplatePopup.html`;

  Office.context.ui.displayDialogAsync(popupUrl, { height: 75, width: 65, displayInIframe: true }, (result) => {
    log.info(result);
    if (result.status === Office.AsyncResultStatus.Failed) {
      log.error("Failed to open dialog:", result.error.message);
    } else {
      popupDialog = result.value;
      popupDialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        async (args: Office.DialogParentMessageReceivedEventArgs) => {
          log.info("Message received from popup:", args);
          await handleMessage(args, popupDialog, undefined);
        }
      );
    }
    if (event) {
      event.completed();
    }
  });
}

export function feedbackSupportPopup(event?: Office.AddinCommands.Event) {
  log.info(process.env.REACT_APP_PUBLIC_URL);
  const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
  const popupUrl = `${frontendUrl}feedbackSupportPopup.html`;

  Office.context.ui.displayDialogAsync(popupUrl, { height: 40, width: 40, displayInIframe: true }, (result) => {
    log.info(result);
    if (result.status === Office.AsyncResultStatus.Failed) {
      log.error("Failed to open dialog:", result.error.message);
    } else {
      const popupDialog = result.value;
      popupDialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args: any) => {
        log.info("Message received from popup:", args.message);
        await handleMessage(args, popupDialog, undefined);
      });
    }
    if (event) {
      event.completed();
    }
  });
}

Office.actions.associate("openPopup", openPopup);
Office.actions.associate("refreshSelection", refreshSelection);
Office.actions.associate("refreshWorksheet", refreshWorksheet);
Office.actions.associate("refreshWorkbook", refreshWorkbook);
Office.actions.associate("unlinkSelection", unlinkSelection);
Office.actions.associate("unlinkWorksheet", unlinkWorksheet);
Office.actions.associate("unlinkWorkbook", unlinkWorkbook);
Office.actions.associate("formatTemplatePopup", formatTemplatePopup);
Office.actions.associate("feedbackSupportPopup", feedbackSupportPopup);
Office.actions.associate("uploadDataPopup", uploadDataPopup);

Office.actions.associate("ShowTaskpane", openPopup);

export async function alphaFormulaBuilder(event?: Office.AddinCommands.Event) {
  if (isActionActive("AlphaFormulaBuilderShortcut")) openPopup(event);
}

export async function alphaAuditHandler(event?: Office.AddinCommands.Event) {
  startProgressPopup(async () => {
    await onAlphaAuditClicked();
  });
  event.completed();
}

export async function refreshSelectionHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("RefreshSelectionShortcut")) await refreshSelection(event);
}

export async function refreshWorksheetHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("RefreshWorksheetShortcut")) await refreshWorksheet(event);
}

export async function refreshWorkbookHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("RefreshWorkbookShortcut")) await refreshWorkbook(event);
}

export async function uploadTemplatesHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("UploadTemplatesShortcut")) openPopup(event);
}

export async function uploadTemplatesCapitalizationHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("UploadTemplatesCapitalizationShortcut")) openPopup(event);
}

export async function uploadBuilderHandler(event?: Office.AddinCommands.Event) {
  await uploadDataToBackend(event);
}

export async function uploadWorkbookHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("UploadWorkbookShortcut")) openPopup(event);
}

export async function productEducationHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("ProductEducationShortcut")) openPopup(event);
}

export async function settingsHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("SettingsShortcut")) openPopup(event);
}

export async function formatTemplatesHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("FormatTemplatesShortcut")) openPopup(event);
}

export async function feedbackAndSupportHandler(event?: Office.AddinCommands.Event) {
  if (isActionActive("FeedbackAndSupportShortcut")) openPopup(event);
}

export async function downloadCapitalizationTemplate(event?: Office.AddinCommands.Event): Promise<void> {
  const templateId = "ALPHASCRIPT_TEMPLATE_CAPITALIZATION_001";
  const templateFileName = "Capitalization_Upload_Template_SHORT_05.03.2025.xlsm";

  let blobUrl: string | null = null;

  try {
    await Office.onReady();
    log.info("Downloading capitalization template");

    const { data } = await httpClient.get("/file/excel-template", {
      params: {
        templateId,
      },
      responseType: "blob",
    });

    const blob = data as Blob;
    blobUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = templateFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    log.info("Capitalization template downloaded successfully with static ID:", templateId);
  } catch (error) {
    log.error("Failed to download capitalization template:", error);
  } finally {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }

    event?.completed();
  }
}

async function signInHandler() {
  if (!isActionActive("SignInShortcut")) return;

  return Office.addin
    .showAsTaskpane()
    .then(() => {
      return;
    })
    .catch((error) => {
      return error.code;
    });
}

// ----- Associate new actions -----
Office.actions.associate("AlphaFormulaBuilderShortcut", alphaFormulaBuilder);
Office.actions.associate("onAlphaAudit", alphaAuditHandler);
Office.actions.associate("RefreshSelectionShortcut", refreshSelectionHandler);
Office.actions.associate("RefreshWorksheetShortcut", refreshWorksheetHandler);
Office.actions.associate("RefreshWorkbookShortcut", refreshWorkbookHandler);
Office.actions.associate("UploadTemplatesShortcut", uploadTemplatesHandler);
Office.actions.associate("UploadTemplatesCapitalizationShortcut", uploadTemplatesCapitalizationHandler);
Office.actions.associate("UploadBuilderShortcut", uploadBuilderHandler);
Office.actions.associate("UploadWorkbookShortcut", uploadWorkbookHandler);
Office.actions.associate("ProductEducationShortcut", productEducationHandler);
Office.actions.associate("SettingsShortcut", settingsHandler);
Office.actions.associate("FormatTemplatesShortcut", formatTemplatesHandler);
Office.actions.associate("FeedbackAndSupportShortcut", feedbackAndSupportHandler);
Office.actions.associate("SignInShortcut", signInHandler);
Office.actions.associate("downloadCapitalizationTemplate", downloadCapitalizationTemplate);
